/**
 * Handles TikTok video embeds initialisation and configuration.
 * Processes sections, configures parameters, and implements lazy loading.
 */
class TikTokVideosSection {
  /**
   * Creates instance and finds all TikTok video sections on the page.
   */
  constructor() {
    this.sections = document.querySelectorAll(".js-tiktok-videos");
    this.init();
  }

  /**
   * Initialises all TikTok video sections found on the page.
   */
  init() {
    this.sections.forEach((section) => {
      this.initSection(section);
    });
  }

  /**
   * Initialises a single TikTok video section.
   * @param {HTMLElement} section - Section element containing TikTok videos
   */
  initSection(section) {
    const tiktokVideoItems = section.querySelectorAll(".js-tiktok-video");
    const shopifyVideoItems = section.querySelectorAll(".js-shopify-video");
    const shopifyGifItems = section.querySelectorAll(".js-shopify-gif");

    // Handle TikTok videos
    tiktokVideoItems.forEach((videoItem, index) => {
      this.setupTikTokVideoItem(videoItem, section, index);
    });

    // Handle Shopify videos
    shopifyVideoItems.forEach((videoItem, index) => {
      this.setupShopifyVideoItem(videoItem, section, index);
    });

    // Handle Shopify GIFs (no special setup needed, just lazy loading)
    shopifyGifItems.forEach((gifItem) => {
      this.setupShopifyGifItem(gifItem);
    });
  }

  /**
   * Sets up individual TikTok video item with embed configuration.
   * Implements lazy loading using IntersectionObserver.
   * @param {HTMLElement} videoItem - Video item element containing iframe
   * @param {HTMLElement} section - Parent section element with configuration data
   * @param {number} index - Video item index (used for autoplayFirst logic)
   */
  setupTikTokVideoItem(videoItem, section, index) {
    const iframe = videoItem.querySelector("iframe");
    const tiktokId = videoItem.dataset.tiktokId;

    if (!iframe || !tiktokId) return;

    // Get settings from section JSON configuration
    // For details on these settings, see https://developers.tiktok.com/doc/embed-player
    const config = JSON.parse(section.dataset.tiktokConfig || '{}');
    const {
      controls = true,
      fullscreen = true,
      loop = false,
      autoplay = false,
      autoplayFirst = false,
      description = true,
      showRelated = false,
      timestamp = false,
      hideProgressBar = false,
      hidePlayButton = false,
      hideVolumeControl = false
    } = config;

    // Determine if video should autoplay based on settings and index
    const shouldAutoplay = autoplay || (autoplayFirst && index === 0);

    // Build URL parameters for TikTok embed
    const urlParams = new URLSearchParams();

    // Configure TikTok embed parameters
    if (!controls) urlParams.append("controls", "0");
    if (!fullscreen) urlParams.append("fullscreen_button", "0");
    if (loop) urlParams.append("loop", "1");
    if (shouldAutoplay) urlParams.append("autoplay", "1");
    if (description) urlParams.append("description", "1");
    if (showRelated) urlParams.append("rel", "1");
    if (timestamp) urlParams.append("timestamp", "0");
    if (hideProgressBar) urlParams.append("progress_bar", "0");
    if (hidePlayButton) urlParams.append("play_button", "0");
    if (hideVolumeControl) urlParams.append("volume_control", "0");

    // Construct full TikTok player URL with configured parameters
    const baseUrl = `https://www.tiktok.com/player/v1/${tiktokId}`;
    const newUrl = urlParams.toString() ? `${baseUrl}?${urlParams.toString()}` : baseUrl;

    iframe.src = newUrl;

    // Implement lazy loading using IntersectionObserver
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Video is now visible, ensure it's loaded with correct URL
              if (iframe.src !== newUrl) {
                iframe.src = newUrl;
              }
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "50px", // Start loading 50px before video comes into view
        }
      );

      observer.observe(videoItem);
    }
  }

  /**
   * Sets up individual Shopify video item with lazy loading.
   * The video_tag filter handles most configuration, so we just need lazy loading.
   * @param {HTMLElement} videoItem - Video item element containing video tag
   * @param {HTMLElement} section - Parent section element with configuration data
   * @param {number} index - Video item index (unused but kept for consistency)
   */
  setupShopifyVideoItem(videoItem, section, index) {
    const video = videoItem.querySelector("video");
    if (!video) return;

    // Implement lazy loading using IntersectionObserver
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Video is now visible, ensure it's loaded
              if (video.readyState === 0) {
                video.load();
              }
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "50px", // Start loading 50px before video comes into view
        }
      );

      observer.observe(videoItem);
    }
  }

  /**
   * Sets up individual Shopify GIF item with lazy loading.
   * @param {HTMLElement} gifItem - GIF item element containing img tag
   */
  setupShopifyGifItem(gifItem) {
    const img = gifItem.querySelector("img");
    if (!img) return;

    // Implement lazy loading using IntersectionObserver
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // GIF is now visible, ensure it's loaded
              if (!img.complete) {
                img.load();
              }
              observer.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "50px", // Start loading 50px before GIF comes into view
        }
      );

      observer.observe(gifItem);
    }
  }
}

/**
 * Initialise TikTokVideosSection when DOM is ready.
 * Handles both cases where script loads before or after DOMContentLoaded.
 */
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new TikTokVideosSection();
  });
} else {
  new TikTokVideosSection();
}

/**
 * Re-initialise TikTokVideosSection for dynamic content.
 * Useful for Shopify theme editor where sections can be added/removed dynamically.
 * @param {Event} event - shopify:section:load event
 */
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector(".js-tiktok-videos")) {
    new TikTokVideosSection();
  }
});
